import { useEffect, useRef, useState, useCallback } from 'react';
import type { ActiveCvTarget, CvFeatureDescriptor } from '@/lib/cv-api';

/**
 * Client-side computer vision scanner for the AR overlay.
 *
 * Captures frames from the live camera video element at a reduced resolution,
 * extracts a lightweight feature descriptor (16-bin color histogram, brightness,
 * edge-score) — the same signature as the backend `cv-features.ts` extractor —
 * and compares it against each active CV target using cosine similarity.
 *
 * Returns the best match above threshold, all match results, and a scanning flag.
 */

export interface CvMatchResult {
  targetId: string;
  targetName: string;
  score: number;
  threshold: number;
  matched: boolean;
}

export interface CvScanState {
  isScanning: boolean;
  bestMatch: CvMatchResult | null;
  allResults: CvMatchResult[];
  /** Bounding box in percentages (0–1) of the video frame. */
  boundingBox: { x: number; y: number; w: number; h: number } | null;
  scanProgress: number;
  /** Evaluates a selected image using the same descriptor comparison as camera frames. */
  scanImageFile: (file: File) => Promise<CvMatchResult | null>;
}

const SCAN_INTERVAL_MS = 600;
const CANVAS_WIDTH = 192;
const CANVAS_HEIGHT = 144;

/** Maximum pixel samples for histogram computation (performance). */
const MAX_PIXEL_SAMPLES = 8000;

/** Grid dimensions for edge-score computation — must match server. */
const GRID_W = 8;
const GRID_H = 6;

/**
 * Extract a feature descriptor from RGBA ImageData — the client-side equivalent
 * of the backend `extractFeatureDescriptor` in `cv-descriptor.ts`.
 *
 * MUST stay in sync with `extractDescriptorFromPixels` in `lib/db/src/cv-descriptor.ts`:
 * 64-bin color histogram (4 levels per channel = 4³), brightness, and edge score
 * over an 8×6 grid.
 */
function extractFrameDescriptor(imageData: ImageData): Omit<CvFeatureDescriptor, 'fileHash' | 'sizeBytes' | 'mimeType'> {
  const { data, width, height } = imageData;

  // ── 64-bin color histogram (4 levels per channel → 4³ = 64) ──────────────
  const buckets = new Array<number>(64).fill(0);
  let totalBrightness = 0;
  let sampleCount = 0;

  const pixelCount = (data.length / 4) | 0;
  const step = Math.max(1, Math.floor(pixelCount / MAX_PIXEL_SAMPLES)) * 4;

  for (let i = 0; i < data.length; i += step) {
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;

    // 4 levels per channel: 2 bits each → bucket index 0–63
    const rq = (r >> 6) & 0x3;
    const gq = (g >> 6) & 0x3;
    const bq = (b >> 6) & 0x3;
    const bucket = (rq << 4) | (gq << 2) | bq;

    buckets[bucket] = (buckets[bucket] ?? 0) + 1;
    totalBrightness += (r + g + b) / 3;
    sampleCount++;
  }

  if (sampleCount === 0) {
    return {
      colorBuckets: new Array(64).fill(1 / 64),
      brightness: 0,
      edgeScore: 0,
      width,
      height,
    };
  }

  const sum = buckets.reduce((a, b) => a + b, 0);
  const colorBuckets = sum > 0 ? buckets.map((b) => b / sum) : new Array(64).fill(1 / 64);
  const brightness = Math.min(1, Math.max(0, totalBrightness / (sampleCount * 255)));

  // ── Edge score — variance of brightness across grid regions ────────────────
  const cellW = Math.max(1, Math.floor(width / GRID_W));
  const cellH = Math.max(1, Math.floor(height / GRID_H));
  const cellBright: number[] = [];

  for (let gy = 0; gy < GRID_H; gy++) {
    for (let gx = 0; gx < GRID_W; gx++) {
      let cellSum = 0;
      let cellCount = 0;
      const startY = gy * cellH;
      const startX = gx * cellW;
      const endY = Math.min(startY + cellH, height);
      const endX = Math.min(startX + cellW, width);

      for (let y = startY; y < endY; y += 2) {
        for (let x = startX; x < endX; x += 2) {
          const idx = (y * width + x) * 4;
          cellSum += (data[idx]! + data[idx + 1]! + data[idx + 2]!) / 3;
          cellCount++;
        }
      }
      cellBright.push(cellCount > 0 ? cellSum / cellCount : 0);
    }
  }

  const meanBright =
    cellBright.length > 0
      ? cellBright.reduce((a, b) => a + b, 0) / cellBright.length
      : 0;
  const variance =
    cellBright.length > 1
      ? cellBright.reduce((acc, v) => acc + (v - meanBright) ** 2, 0) / cellBright.length
      : 0;
  const edgeScore = Math.min(1, variance / (128 * 128));

  return { colorBuckets, brightness, edgeScore, width, height };
}

/**
 * Compare two descriptors — same weighted blend as the backend
 * `compareDescriptors`: 70% color cosine + 20% brightness + 10% edge.
 * MUST stay in sync with `lib/db/src/cv-descriptor.ts`.
 */
function compareDescriptors(
  a: CvFeatureDescriptor | null | undefined,
  b: CvFeatureDescriptor | null | undefined
): number {
  if (!a || !b) return 0;

  let dot = 0;
  let magA = 0;
  let magB = 0;
  const len = Math.min(a.colorBuckets.length, b.colorBuckets.length, 64);
  for (let i = 0; i < len; i++) {
    const av = a.colorBuckets[i]!;
    const bv = b.colorBuckets[i]!;
    dot += av * bv;
    magA += av * av;
    magB += bv * bv;
  }
  const cosine = magA > 0 && magB > 0 ? dot / Math.sqrt(magA * magB) : 0;

  const brightnessScore = 1 - Math.min(1, Math.abs(a.brightness - b.brightness));
  const edgeScore = 1 - Math.min(1, Math.abs(a.edgeScore - b.edgeScore));

  const score = 0.7 * cosine + 0.2 * brightnessScore + 0.1 * edgeScore;
  return Math.min(1, Math.max(0, score));
}

export function useCvScanner(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  targets: ActiveCvTarget[]
): CvScanState {
  const [state, setState] = useState<Omit<CvScanState, 'scanImageFile'>>({
    isScanning: false,
    bestMatch: null,
    allResults: [],
    boundingBox: null,
    scanProgress: 0,
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastScanRef = useRef<number>(0);
  const scanProgressRef = useRef<number>(0);

  const evaluateDescriptor = useCallback((frameDescriptor: Omit<CvFeatureDescriptor, 'fileHash' | 'sizeBytes' | 'mimeType'>) => {
    const results: CvMatchResult[] = targets.map((target) => {
      const score = compareDescriptors(target.descriptor, {
        ...target.descriptor,
        ...frameDescriptor,
      } as CvFeatureDescriptor);
      const rounded = Math.round(score * 1000) / 1000;
      return {
        targetId: target.id,
        targetName: target.name,
        score: rounded,
        threshold: target.confidenceThreshold,
        matched: rounded >= target.confidenceThreshold,
      };
    });

    results.sort((a, b) => b.score - a.score);
    const best = results[0] ?? null;
    setState((current) => ({
      ...current,
      isScanning: true,
      bestMatch: best?.matched ? best : null,
      allResults: results,
      boundingBox: best?.matched ? { x: 0.2, y: 0.25, w: 0.6, h: 0.5 } : null,
    }));
    return best?.matched ? best : null;
  }, [targets]);

  const scan = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || targets.length === 0) {
      setState((s) => ({ ...s, isScanning: false, bestMatch: null, allResults: [] }));
      return;
    }

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
      canvasRef.current.width = CANVAS_WIDTH;
      canvasRef.current.height = CANVAS_HEIGHT;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    try {
      ctx.drawImage(video, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    } catch {
      return; // video not ready
    }

    const imageData = ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    const frameDescriptor = extractFrameDescriptor(imageData);

    evaluateDescriptor(frameDescriptor);
  }, [videoRef, targets.length, evaluateDescriptor]);

  // Animation loop — scan on an interval; throttle progress state so we do not
  // re-render the AR page every frame (that detaches the camera <video> ref).
  useEffect(() => {
    if (targets.length === 0) {
      setState((s) => ({ ...s, isScanning: false, bestMatch: null, allResults: [], boundingBox: null, scanProgress: 0 }));
      return;
    }

    let mounted = true;
    let lastProgressPublish = 0;
    const PROGRESS_PUBLISH_MS = 100;

    const tick = (now: number) => {
      if (!mounted) return;

      if (now - lastScanRef.current >= SCAN_INTERVAL_MS) {
        lastScanRef.current = now;
        scan();
      }

      scanProgressRef.current = (now % 2000) / 2000;
      if (now - lastProgressPublish >= PROGRESS_PUBLISH_MS) {
        lastProgressPublish = now;
        const progress = scanProgressRef.current;
        setState((s) => (s.scanProgress === progress ? s : { ...s, scanProgress: progress }));
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      mounted = false;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [scan, targets.length]);

  const scanImageFile = useCallback(async (file: File): Promise<CvMatchResult | null> => {
    if (!file.type.startsWith('image/') || targets.length === 0) return null;

    const imageUrl = URL.createObjectURL(file);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error('The selected image could not be read.'));
        element.src = imageUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = CANVAS_WIDTH;
      canvas.height = CANVAS_HEIGHT;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('Image processing is unavailable in this browser.');
      context.drawImage(image, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      return evaluateDescriptor(extractFrameDescriptor(context.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)));
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  }, [evaluateDescriptor, targets.length]);

  return { ...state, scanImageFile };
}
